// "You are here": a line across the week at the current time. Place it in a
// relative box that spans exactly the ten slot columns. Accent, not danger:
// red already means a blocking rule. Dimmed outside working hours.
import type { Day, Half } from "../lib/types";
import { clock, HALF_TIMES } from "../lib/days";

export interface NowLineProps {
  days: Day[];
  now: { day: Day; half: Half; minute: number } | null;
  night?: boolean;
}

/** Fraction of the week (0..1) the time sits at, or null when it's outside the week. */
export function nowFraction(days: Day[], now: NowLineProps["now"]): { at: number; off: boolean } | null {
  if (!now) return null;
  const di = days.indexOf(now.day);
  if (di < 0) return null;
  const am = HALF_TIMES.AM, pm = HALF_TIMES.PM;
  let slot: number, within: number, off = false;
  if (now.minute < am.start) { slot = 0; within = 0; off = true; }
  else if (now.minute <= am.end) { slot = 0; within = (now.minute - am.start) / (am.end - am.start); }
  else if (now.minute < pm.start) { slot = 1; within = 0; off = true; }
  else if (now.minute <= pm.end) { slot = 1; within = (now.minute - pm.start) / (pm.end - pm.start); }
  else { slot = 1; within = 1; off = true; }
  return { at: (di * 2 + slot + within) / (days.length * 2), off };
}

export function NowLine({ days, now, night }: NowLineProps) {
  const f = nowFraction(days, now);
  if (!f || !now) return null;
  const cls = ["nowline", night ? "nowline--night" : "", f.at > 0.85 ? "nowline--left" : "", f.off ? "is-off" : ""].filter(Boolean).join(" ");
  return (
    <div className={cls} style={{ left: `${f.at * 100}%` }} aria-label={`Now, ${clock(now.minute)}`} data-testid="nowline">
      <span className="nowline__t">{clock(now.minute)}</span>
    </div>
  );
}
